import {
  useInvalidate,
  useTranslate,
  Authenticated,
  CanAccess,
  useGo,
  useList,
} from "@refinedev/core";
import { Button, Space, Table, TableColumnsType } from "antd";
import type { VoiceProfileResponse } from "../types";
import { useOrganization } from "hooks/useOrganization";
import { Uuid } from "../../../components/UUID";
import { PlusOutlined, SyncOutlined } from "@ant-design/icons";
import { CreateButton, DateField, List } from "@refinedev/antd";
import { addDateTz } from "../utils";
import { useState } from "react";
import VoiceItem from "../components/ContactWithPresenter/VoiceItem";
import { DeleteButtonWithErrorPopover } from "components/DeleteButtonWithErrorPopover/";

export const VoiceProfilesListPage = () => {
  const t = useTranslate();
  const invalidate = useInvalidate();
  const { organization } = useOrganization({});
  const resource = `media/${organization?.id}/voice_profiles`;
  const { data, isLoading, refetch, isRefetching } =
    useList<VoiceProfileResponse>({
      resource,
      queryOptions: {
        enabled: Boolean(organization),
      },
    });

  const go = useGo();
  const [playingId, setPlayingId] = useState<string | null>(null);

  const columns: TableColumnsType<VoiceProfileResponse> = [
    {
      title: t("media.voices.index.id"),
      dataIndex: "id",
      key: "id",
      render: (id: string) => <Uuid id={id} />,
    },
    {
      title: t("media.voices.index.name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("media.voices.index.type"),
      dataIndex: "category",
      key: "category",
    },
    {
      title: t("media.voices.index.creationDate"),
      dataIndex: "created",
      key: "created",
      render: (text) => <DateField value={addDateTz(text)} format="LLL" />,
    },
    {
      title: t("media.voices.index.preview"),
      key: "preview",
      width: 60,
      render: (record) => (
        <VoiceItem
          id={record.id}
          voice={record}
          onClick={() => {}}
          onClickPlay={(voice) => setPlayingId(voice?.id)}
          currentlyPlayedVoice={data?.data?.find((v) => v.id === playingId)}
        />
      ),
    },
    {
      title: t("media.voices.index.actions"),
      key: "actions",
      render: (record) => (
        <CanAccess resource="media_voices" action="delete">
          <DeleteButtonWithErrorPopover
            size="small"
            shape="round"
            resource={`media/voice_profiles`}
            recordItemId={record.id}
            accessControl={{ enabled: false }}
            onSuccess={() => {
              invalidate({
                resource,
                invalidates: ["list"],
              }).catch((error) => {
                console.error("Failed to invalidate projects list", error);
              });
            }}
          />
        </CanAccess>
      ),
    },
  ];

  return (
    <Authenticated key="voice_list">
      <List
        headerProps={{
          title: t("src.App.voices"),
        }}
        headerButtons={({ createButtonProps }) => (
          <Space>
            <Button
              icon={<SyncOutlined />}
              onClick={() => {
                refetch().catch((error) => {
                  console.error("Failed to refetch voice profiles list", error);
                });
              }}
              disabled={isRefetching}
              shape="circle"
              type="text"
            />
            {createButtonProps && (
              <CreateButton
                {...createButtonProps}
                icon={<PlusOutlined />}
                shape="round"
              >
                {t("media.voices.index.createAVoice")}
              </CreateButton>
            )}
          </Space>
        )}
      >
        <Table<VoiceProfileResponse>
          loading={isLoading}
          dataSource={data?.data}
          columns={columns}
          pagination={{ pageSize: 50 }}
          onRow={(record) => ({
            onClick: (event) => {
              const target = event.target as HTMLElement;
              if (!target.closest(".ant-btn")) {
                go({
                  to: {
                    resource: "media_voices",
                    id: record.id,
                    action: "show",
                  },
                });
              }
            },
            style: { cursor: "pointer" },
          })}
        />
      </List>
    </Authenticated>
  );
};
